import { useState } from "react";

import { homesOnboardingApi } from "libs/api";
import type { PropertySelectionItem as HmPropertySelectionItem } from "libs/codegen/homes-onbaording";
import { useNavigate, useParams } from "router";
import type { FooterProps } from "../Footer";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { ValidationError } from "./constants";
import { usePropertiesValue } from "./propertiesAtom";
import { selectablePropertiesFilter } from "./selectablePropertiesFilter";
import { useSelectedPropertiesValue } from "./selectedPropertiesAtom";

export const useAutomaticListsHandler = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ValidationError>(null);

    const {
        automaticPropertySelectionCms: { back, importListings, importListingsWithNumber, goToListing },
    } = usePropertySelectionViewModelValue();
    const navigate = useNavigate();
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic/lists");

    const selectedProperties = useSelectedPropertiesValue();
    const properties = usePropertiesValue();

    const selectableProperties = properties.filter(selectablePropertiesFilter);

    const onSecondary = () => {
        navigate("/:lang/list-my-property/get-started/automatic", {
            params: {
                lang,
            },
        });
    };

    const onPrimary = async () => {
        // if no selectable properties, go to listing page
        if (selectableProperties.length === 0) {
            window.location.assign(`/homes/${lang}/HostManage/#/listings`);
        } else {
            setLoading(true);
            setError(null);

            if (selectedProperties.length === 0) {
                setError(ValidationError.EmptySelection);
                setLoading(false);
            } else {
                try {
                    const { data } = await homesOnboardingApi.importListing(lang, {
                        selected: selectableProperties.filter((o) =>
                            selectedProperties.includes(o.id),
                        ) as HmPropertySelectionItem[],
                        unselected: selectableProperties.filter(
                            (o) => !selectedProperties.includes(o.id),
                        ) as HmPropertySelectionItem[],
                    });
                    if (data.inProgressBatchExist) {
                        setError(ValidationError.InprogressExist);
                        setLoading(false);
                    } else {
                        window.location.assign(data.redirectTo);
                    }
                } catch {
                    setError(ValidationError.TechnicalIssue);
                    setLoading(false);
                }
            }
        }
    };

    const footerProps: FooterProps = {
        secondary: {
            label: back,
            dataElementName: "back",
        },
        primary: {
            // eslint-disable-next-line no-nested-ternary
            label:
                selectableProperties.length === 0
                    ? goToListing
                    : selectedProperties.length === 0
                      ? importListings
                      : importListingsWithNumber.replace("{0}", selectedProperties.length.toString()),
            loading,
            dataElementName: "import-button",
        },
        onSecondary,
        onPrimary,
    };

    return {
        ...footerProps,
        error,
    };
};
